/**
 * Post-login landing resolver (mirrors asset-dashboard landing-prefs).
 * Portal FE uses this so landing works even if asset-dashboard BE is down.
 */

import { getServiceUrl } from '@shared/config';

export type ExecutivePersona = 'financial' | 'operation' | 'security';

export type LandingResolveInput = {
  roles?: string[];
  teamIds?: string[];
  userId?: string;
  userType?: string;
};

export type LandingResolveResult = {
  landing: string;
  persona: ExecutivePersona | null;
  ruleId: string | null;
};

const ASSET_DASHBOARD_PORT = 4015;

const PREFS = {
  defaultLanding: '/entry',
  rules: [
    {
      id: 'admin-executive',
      match: { rolesAny: ['tenant_admin'], userIdsAny: ['iampam-admin'] },
      persona: 'financial' as ExecutivePersona,
      priority: 10,
    },
    {
      id: 'finance-cfo',
      match: { rolesAny: ['role-finance', 'role-cfo'], teamIdsAny: ['finance', 'Finance', 'CFO', '총무'] },
      persona: 'financial' as ExecutivePersona,
      priority: 15,
    },
    {
      id: 'security-ciso',
      match: { rolesAny: ['role-security'], teamIdsAny: ['security', 'Security', 'SEC'] },
      persona: 'security' as ExecutivePersona,
      priority: 20,
    },
    {
      id: 'devops-cio',
      match: { rolesAny: ['role-devops'], teamIdsAny: ['DevOps', 'devops', 'OPS', 'infra'] },
      persona: 'operation' as ExecutivePersona,
      priority: 30,
    },
  ],
};

function executiveUrl(persona?: ExecutivePersona | null): string {
  const base = getServiceUrl(ASSET_DASHBOARD_PORT, '/asset-dashboard/');
  if (!persona) return base;
  const join = base.includes('?') ? '&' : '?';
  return `${base}${join}persona=${persona}`;
}

export function resolvePostLoginLanding(input: LandingResolveInput): LandingResolveResult {
  if (input.userType === 'portal-admin') {
    return { landing: '/entry', persona: null, ruleId: null };
  }

  const roleSet = new Set((input.roles || []).map(String));
  const teamSet = new Set((input.teamIds || []).map(String));
  const rules = [...PREFS.rules].sort((a, b) => a.priority - b.priority);

  for (const rule of rules) {
    const m = rule.match;
    const roleHit = (m.rolesAny || []).some((r) => roleSet.has(r));
    const teamHit = (m.teamIdsAny || []).some((t) => teamSet.has(t));
    const userHit = (m.userIdsAny || []).includes(input.userId || '');
    if (roleHit || teamHit || userHit) {
      return {
        landing: executiveUrl(rule.persona),
        persona: rule.persona,
        ruleId: rule.id,
      };
    }
  }

  return { landing: PREFS.defaultLanding, persona: null, ruleId: null };
}

export function toAssetDashboardLanding(persona?: ExecutivePersona | null): string {
  return executiveUrl(persona);
}
